import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Checkbox,
  Button,
  Result,
  notification,
  Modal,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { FormattedMessage } from "react-intl";
import LoginTemplate from "../../../components/templates/LoginTemplate";
import ChildrenAnimationWrapper from "../../../components/animations/ChildrenAnimationWrapper";
import AnimationChildWrapper from "../../../components/animations/AnimationChildWrapper";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { finishRegistration } from "../registrationSlice";
import { ReactComponent as Forgot } from "../../../assets/forgot-password.svg";
import Auth from "../../../api/auth";

const ChangePassword = () => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const [error, setError] = useState(null);
  const [mode, setMode] = useState(null);
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [tenantId, setTenantId] = useState({});
  const { invitationAccepted, error: registrationError } = useSelector(
    (state) => state.registration
  );
  const registrationInProgress = useSelector(
    (state) => state.registration.registrationInProgress
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const modeParam = urlParams.get("mode");
    setMode(modeParam);
    const tenantId = urlParams.get("tenantId");
    if (!tenantId) {
      setError("Wrong registration link");
    }
    setTenantId(tenantId);
    if (modeParam !== "resetPassword") {
      Auth.signOut();
    }
  }, []);

  const handleTermsModalOpen = () => {
    setTermsModalVisible(true);
  };

  const handleTermsModalClose = () => {
    setTermsModalVisible(false);
  };

  const onFinish = async (values) => {
    const urlParams = new URLSearchParams(window.location.search);
    if (mode === "resetPassword") {
      var actionCode = urlParams.get("oobCode");
      Auth.changePassword(tenantId, actionCode, values.password)
        .then(() => {
          history.push("/login");
          notification.success({
            message: intl.formatMessage({
              id: "app.forms.user.changePassSuccessNotify",
            }),
          });
        })
        .catch((err) => {
          setError(err);
          notification.error({
            message: intl.formatMessage({
              id: "app.forms.user.changePassFailedNotify",
            }),
          });
        });
    } else {
      const params = {
        tenantId,
        email: values.username,
        newPassword: values.password,
      };
      dispatch(finishRegistration(params));
    }
  };

  if (error || registrationError) {
    return (
      <Result
        status="warning"
        title={intl.formatMessage({
          id: "app.errors.failFetchInvitation",
        })}
        extra={
          <Button
            type="primary"
            key="console"
            onClick={() => {
              history.push("/");
            }}
          >
            {intl.formatMessage({
              id: "app.actions.goHome",
            })}
          </Button>
        }
      />
    );
  }

  if (!tenantId) {
    return null; //show loading
  }

  if (invitationAccepted) {
    return <Redirect to="/" />;
  }

  var passw = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{6,20}$/;
  return (
    <LoginTemplate
      title={intl.formatMessage({
        id: "app.forms.user.set_password_title",
      })}
      subTitle={intl.formatMessage({
        id: "app.forms.user.set_password_subtitle",
      })}
      image={<Forgot />}
    >
      <Form form={form} name="register" onFinish={onFinish} scrollToFirstError>
        <ChildrenAnimationWrapper>
          <AnimationChildWrapper itemKey={1}>
            <Form.Item name="username" hasFeedback>
              <Input
                prefix={<UserOutlined />}
                placeholder={intl.formatMessage({
                  id: "app.forms.user.username",
                })}
              />
            </Form.Item>
          </AnimationChildWrapper>
          <AnimationChildWrapper itemKey={2}>
            <Form.Item
              validateTrigger={"onSubmit"}
              name="password"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "app.forms.user.password_required",
                  }),
                },
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    if (value.match(passw)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      intl.formatMessage({
                        id: "app.forms.strongPasswordMessage",
                      })
                    );
                  },
                }),
              ]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={intl.formatMessage({
                  id: "app.forms.user.password",
                })}
              />
            </Form.Item>
          </AnimationChildWrapper>
          <AnimationChildWrapper itemKey={3}>
            <Form.Item
              name="confirm"
              dependencies={["password"]}
              hasFeedback
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "app.forms.user.password_confirm_message",
                  }),
                },
                ({ getFieldValue }) => ({
                  validator(rule, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }

                    return Promise.reject(
                      intl.formatMessage({
                        id: "app.forms.user.password_not_match",
                      })
                    );
                  },
                }),
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder={intl.formatMessage({
                  id: "app.forms.user.confirm_password",
                })}
              />
            </Form.Item>
          </AnimationChildWrapper>
          <AnimationChildWrapper itemKey={4}>
            <Form.Item
              name="agreement"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          intl.formatMessage({
                            id: "app.forms.user.should_accept_agreement",
                          })
                        ),
                },
              ]}
            >
              <Checkbox>
                <FormattedMessage id="app.forms.user.read_statement" />
                <a onClick={handleTermsModalOpen}>
                  <FormattedMessage id="app.forms.user.terms" />
                </a>
              </Checkbox>
            </Form.Item>
          </AnimationChildWrapper>
          <AnimationChildWrapper itemKey={5}>
            <Form.Item>
              <Button
                block
                type="primary"
                htmlType="submit"
                loading={registrationInProgress}
              >
                <FormattedMessage id="app.forms.user.register" />
              </Button>
            </Form.Item>
          </AnimationChildWrapper>
        </ChildrenAnimationWrapper>
      </Form>
      <Modal
        title={intl.formatMessage({ id: "app.forms.user.terms" })}
        open={termsModalVisible}
        onCancel={handleTermsModalClose}
        footer={null}
        width={"100vw"}
        style={{
          height: "90vh",
          maxWidth: "90vw",
          position: "absolute",
          top: "5vh",
          right: "5vw",
        }}
        styles={{
          body: {
            height: "80vh",
            width: "85vw",
            padding: "5vh 2vw",
          },
        }}
      >
        <embed
          src="./nda.pdf"
          type="application/pdf"
          width="100%"
          height="100%"
        />
      </Modal>
    </LoginTemplate>
  );
};

export default ChangePassword;
